import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useStoreDetail } from '@/features/stores';
import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { displayTimeZone, useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { CallbackNotice } from '../components/callback-notice';
import { PinterestCard } from '../components/pinterest-card';
import { useSocialConnections } from '../hooks/use-social-connections';
import { findConnection, type ChannelsSearch } from '../schemas/channel-state';

export type PinterestConnectionPageProps = ChannelsSearch;

function Connection({ connected, error }: PinterestConnectionPageProps) {
  const { t } = useTranslation('social');
  const store = useStore();
  const navigate = useNavigate();
  const { editable, reason } = useStoreEditable({ permission: 'social.write' });
  const connections = useSocialConnections(store.id);
  const detail = useStoreDetail(store.id);
  const pinterest = connections.data
    ? findConnection(connections.data.items, 'pinterest')
    : undefined;

  const dismiss = () =>
    void navigate({
      to: '/s/$storeId/social/pinterest/connection',
      params: { storeId: store.id },
      replace: true,
    });

  return (
    <PageContent width="narrow">
      <PageHeader
        title={t('connection.pageTitle')}
        description={t('connection.pageDescription', { store: store.name })}
      />
      <ReadOnlyBanner reason={reason} />
      <CallbackNotice connected={connected} error={error} onDismiss={dismiss} />
      {connections.isPending ? (
        <Skeleton className="h-96" />
      ) : connections.error ? (
        <ErrorState error={connections.error} onRetry={() => void connections.refetch()} />
      ) : (
        <PinterestCard
          storeId={store.id}
          connection={pinterest}
          primaryDomain={detail.isPending ? undefined : (detail.data?.primaryDomain ?? null)}
          editable={editable}
          timeZone={displayTimeZone(store.timezone)}
        />
      )}
    </PageContent>
  );
}

/** Pinterest › Connection: the account, the website claim, catalogue sync and the ads step. */
export function PinterestConnectionPage(props: PinterestConnectionPageProps) {
  return (
    <FeatureErrorBoundary name="social.connection">
      <Connection {...props} />
    </FeatureErrorBoundary>
  );
}
